/**
 * The rules behind the layout view of the integrated resources: how a court is
 * divided by the ways it is sold.
 *
 * A court (an integrated resource) is sold through its resource group: each
 * split of the group is one way of selling it, and the split's resources are the
 * parts the court is divided into that way (1 = the whole court, 6 = sixths).
 *
 * Pure functions, no UI: an offer is `{ units }`, with `units` the number of parts.
 */

const gcd = (a, b) => (b ? gcd(b, a % b) : a);
const lcm = (a, b) => (a * b) / gcd(a, b);

/** Rows and columns of a court divided into `cells`: two rows when it splits evenly in four or more. */
export function gridShape(cells) {
  const rows = cells >= 4 && cells % 2 === 0 ? 2 : 1;
  return [rows, cells / rows];
}

/**
 * For a court sold in `offers`: the cells of a grid every offer divides evenly,
 * and the size in cells of a part of each offer.
 */
export function computeRules(offers) {
  const cells = offers.reduce((acc, offer) => lcm(acc, offer.units), 1);
  return { cells, ks: offers.map(offer => cells / offer.units) };
}

/**
 * The hall a court belongs to, from its name: everything before the last
 * " - " ("Hal 3 - Dobbelthal - Bane 3-1" is in "Hal 3 · Dobbelthal"), or null
 * when the name has no such part.
 */
export function hallOf(name) {
  const parts = (name || '').split(' - ');
  return parts.length > 1 ? parts.slice(0, -1).join(' · ') : null;
}

const commonPrefix = texts => texts.reduce((prefix, text) => {
  let length = 0;
  while (length < prefix.length && prefix[length] === text[length]) length++;
  return prefix.slice(0, length);
});

/**
 * Names split into what they share and what tells them apart, cut at spaces so
 * no word is broken: "Badminton 1", "Badminton 2" → prefix "Badminton ", parts
 * "1", "2". A single name, or names one of which would be left empty, are not
 * split: everything is in `parts`.
 */
export function splitCommonParts(names) {
  const whole = { prefix: '', suffix: '', parts: names };
  if (new Set(names).size < 2) return whole;

  const rawPrefix = commonPrefix(names);
  const prefix = rawPrefix.slice(0, rawPrefix.lastIndexOf(' ') + 1);
  const rests = names.map(name => name.slice(prefix.length));

  const reversed = rests.map(rest => [...rest].reverse().join(''));
  const rawSuffix = [...commonPrefix(reversed)].reverse().join('');
  const space = rawSuffix.indexOf(' ');
  const suffix = space < 0 ? '' : rawSuffix.slice(space);

  const parts = rests.map(rest => rest.slice(0, rest.length - suffix.length).trim());
  if (parts.some(part => part === '')) return whole;

  return { prefix: prefix.trim(), suffix: suffix.trim(), parts };
}
